import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { usePushSubscription } from "@base/model/hooks/usePushSubscription";
import { Button } from "@buildblockinc/buildblock-design-system";
import { Bell, BellOff, BellRing } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const ACTIVE_CARD_VISIBLE_MS = 3000;
const ACTIVE_CARD_FADEOUT_MS = 300;

export function NotificationCard() {
  const { status, isProcessing, subscribe } = usePushSubscription();
  const prevStatusRef = useRef(status);
  const [activeCardVisible, setActiveCardVisible] = useState(false);
  const [activeCardLeaving, setActiveCardLeaving] = useState(false);

  useEffect(
    function showActiveCardOnSubscribe() {
      const justSubscribed = prevStatusRef.current === "inactive" && status === "active";
      prevStatusRef.current = status;

      if (!justSubscribed) return;

      setActiveCardVisible(true);
      setActiveCardLeaving(false);

      const fadeTimer = setTimeout(() => setActiveCardLeaving(true), ACTIVE_CARD_VISIBLE_MS);
      const removeTimer = setTimeout(
        () => setActiveCardVisible(false),
        ACTIVE_CARD_VISIBLE_MS + ACTIVE_CARD_FADEOUT_MS,
      );

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    },
    [status],
  );

  if (status === "loading") return <SkeletonCard />;
  if (status === "unsupported") return null;
  if (status === "permission-denied") return <DeniedCard />;
  if (status === "active") {
    if (!activeCardVisible) return null;
    return <ActiveCard isLeaving={activeCardLeaving} />;
  }

  return <InactiveCard onEnable={subscribe} isProcessing={isProcessing} />;
}

function InactiveCard({ onEnable, isProcessing }: { onEnable: () => Promise<boolean>; isProcessing: boolean }) {
  useEffect(function trackPromptView() {
    EventLogger.view(CLIENT_EVENT.PUSH_PROMPT_VIEW);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 shadow-box ring-1 ring-blue-100/60 md:border md:border-gray-200 md:ring-0">
      <div className="flex items-start gap-3.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-circle bg-blue-400/10">
          <Bell className="h-[18px] w-[18px] text-blue-400" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="text-subtitle5 text-gray-1000">Don't miss any updates</h3>
          <p className="text-body4 text-gray-600 leading-relaxed">
            Get notified when someone comments on your posts or new activity happens.
          </p>
        </div>
      </div>

      <Button
        onClick={async () => {
          EventLogger.click(CLIENT_EVENT.PUSH_ENABLE_CLICK);
          const result = await onEnable();
          if (result) {
            EventLogger.result(CLIENT_EVENT.PUSH_SUBSCRIBE_SUCCESS);
          } else {
            EventLogger.result(CLIENT_EVENT.PUSH_SUBSCRIBE_FAIL);
            toast.error("Failed to enable notifications. Please try again.");
          }
        }}
        disabled={isProcessing}
        className="mt-3.5 w-full border-blue-400 text-blue-400"
        color="info"
        variant="outline"
        isLoading={isProcessing}
      >
        Turn on notifications
      </Button>
    </div>
  );
}

function ActiveCard({ isLeaving }: { isLeaving: boolean }) {
  return (
    <div
      className="flex items-center gap-3.5 rounded-8 bg-emerald-50/60 p-4 shadow-box ring-1 ring-emerald-200/50 transition-all duration-300 md:border md:border-gray-200 md:ring-0"
      style={{ opacity: isLeaving ? 0 : 1, transform: isLeaving ? "scale(0.97)" : "scale(1)" }}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-circle bg-emerald-400/10">
        <BellRing className="h-4 w-4 text-emerald-600" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-subtitle6 text-gray-1000">Notifications are on</span>
        <span className="text-body4 text-gray-500">We'll let you know about new activity.</span>
      </div>
    </div>
  );
}

function DeniedCard() {
  const guide = getDeniedGuide();

  useEffect(function trackDeniedView() {
    EventLogger.view(CLIENT_EVENT.PUSH_DENIED_VIEW);
  }, []);

  return (
    <div className="rounded-8 bg-gray-50 p-4 shadow-box ring-1 ring-gray-200/60 md:border md:border-gray-200 md:ring-0">
      <div className="flex items-center gap-3.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-circle bg-gray-200/60">
          <BellOff className="h-4 w-4 text-gray-400" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-subtitle6 text-gray-800">Notifications blocked</span>
          <span className="text-body4 text-gray-500">To receive updates, allow notifications:</span>
        </div>
      </div>

      <ol className="mt-3 ml-[52px] flex flex-col gap-1">
        {guide.map((step, i) => (
          <li key={step} className="text-body4 text-gray-600 leading-relaxed">
            <span className="text-gray-400 mr-1">{i + 1}.</span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}

function getDeniedGuide(): string[] {
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true);

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isStandalone && isIOS) {
    return ["Open the Settings app", "Tap Notifications", "Find Bricks and turn on Allow Notifications"];
  }

  if (isStandalone) {
    return ["Long-press the Bricks app icon", "Tap App info", "Tap Notifications and turn them on"];
  }

  if (isIOS) {
    return ["Add this app to your Home Screen first", "Then enable notifications in Settings → Notifications → Bricks"];
  }

  return ["Tap the lock icon in the address bar", "Tap Site settings", "Set Notifications to Allow"];
}

function SkeletonCard() {
  return (
    <div className="flex items-center gap-3.5 rounded-8 bg-white p-4 shadow-box md:border md:border-gray-200">
      <div className="h-9 w-9 shrink-0 rounded-circle skeleton-shimmer" />
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="h-4 w-28 rounded-2 skeleton-shimmer" />
        <div className="h-3.5 w-44 rounded-2 skeleton-shimmer" />
      </div>
    </div>
  );
}
